import React, { useState } from 'react';
import { Code2, Server, Smartphone, Database, Cpu, ShoppingBag } from 'lucide-react';

export const TechStack: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('Frontend');

  const techCategories = [
    {
      name: 'Frontend',
      icon: Code2,
      description: 'Modern, ultra-fast client interfaces with accessible component architectures.',
      items: [
        { name: 'React', level: 'Core Enterprise Framework' },
        { name: 'TypeScript', level: 'Strict Type-Safety' },
        { name: 'Next.js', level: 'SSR & Edge Streaming' },
        { name: 'Tailwind CSS', level: 'Modern Design Tokens' },
        { name: 'Vue.js', level: 'Component Architecture' },
        { name: 'Vite', level: 'Lightning Build Tooling' }
      ]
    },
    {
      name: 'Backend & APIs',
      icon: Server,
      description: 'High-concurrency microservices, robust data validation, and resilient API contracts.',
      items: [
        { name: 'Node.js', level: 'Asynchronous Event-Driven' },
        { name: 'Python / FastAPI', level: 'High-Throughput APIs' },
        { name: 'GraphQL & REST', level: 'Optimized Data Schemas' },
        { name: 'Go / Golang', level: 'Ultra Low-Latency Services' },
        { name: 'NestJS', level: 'Enterprise Modular Backend' },
        { name: 'Express', level: 'Lightweight Web Services' }
      ]
    },
    {
      name: 'Mobile Development',
      icon: Smartphone,
      description: 'Seamless cross-platform applications providing native 60fps responsiveness.',
      items: [
        { name: 'React Native', level: 'Cross-Platform Framework' },
        { name: 'Flutter', level: 'Multi-Platform Engine' },
        { name: 'Swift (iOS)', level: 'Apple Ecosystem' },
        { name: 'Kotlin (Android)', level: 'Android Platform' },
        { name: 'Expo', level: 'Rapid Mobile Tooling' },
        { name: 'Firebase', level: 'Mobile Auth & Push' }
      ]
    },
    {
      name: 'Databases & Cloud',
      icon: Database,
      description: 'Scalable data storage, zero-downtime backups, and edge cloud infrastructure.',
      items: [
        { name: 'PostgreSQL', level: 'Relational ACID Data' },
        { name: 'MongoDB', level: 'Document Store' },
        { name: 'Redis', level: 'In-Memory Caching & Queues' },
        { name: 'Amazon Web Services', level: 'Cloud Compute & Storage' },
        { name: 'Docker & Kubernetes', level: 'Container Orchestration' },
        { name: 'Cloudflare Edge', level: 'Global CDN & Security' }
      ]
    },
    {
      name: 'AI & Automation',
      icon: Cpu,
      description: 'Generative intelligence, vector search embeddings, and autonomous business workflows.',
      items: [
        { name: 'OpenAI API & Anthropic', level: 'Foundation LLMs' },
        { name: 'Pinecone / ChromaDB', level: 'Vector Knowledge Bases' },
        { name: 'LangChain & LlamaIndex', level: 'Context Orchestration' },
        { name: 'Semantic Search', level: 'Vector Information Retrieval' },
        { name: 'Custom AI Agents', level: 'Workflow Automation' },
        { name: 'HuggingFace', level: 'Open Source Models' }
      ]
    },
    {
      name: 'E-Commerce & ERP',
      icon: ShoppingBag,
      description: 'End-to-end commerce checkout engines, financial pipelines, and enterprise integrations.',
      items: [
        { name: 'Stripe & PayPal', level: 'Global Payments & Subscriptions' },
        { name: 'Custom ERP Engines', level: 'Enterprise Workflows' },
        { name: 'Shopify Plus Headless', level: 'Modern Commerce' },
        { name: 'WooCommerce Custom', level: 'Flexible Storefronts' },
        { name: 'Webhook Pipelines', level: 'Real-Time Event Sync' },
        { name: 'Single Sign-On (SSO)', level: 'Enterprise Identity' }
      ]
    }
  ];

  const currentGroup = techCategories.find(c => c.name === activeCategory) || techCategories[0];

  return (
    <section className="bg-[#071426] py-20 border-b border-[#1b2f4a] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-8 bg-[#FF851B]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B]">
              MODERN TECH STACK
            </span>
            <span className="h-px w-8 bg-[#FF851B]" />
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display">
            Technology & Capabilities
          </h2>

          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            We build with battle-tested frameworks, cloud infrastructure, and modern AI pipelines chosen for reliability and scale.
          </p>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2.5 mb-10">
          {techCategories.map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.name;
            return (
              <button
                key={cat.name}
                onClick={() => setActiveCategory(cat.name)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 ${
                  isActive 
                    ? 'bg-[#159DE8] text-white shadow-glow-blue' 
                    : 'bg-[#0c1a2d] border border-[#1b2f4a] text-[#8FA0B8] hover:text-white hover:bg-[#10223b]'
                }`}
              >
                <Icon size={14} />
                <span>{cat.name}</span>
              </button>
            );
          })}
        </div>

        {/* Category Detail Box */}
        <div className="p-8 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] shadow-card-dark">
          <div className="mb-6 pb-4 border-b border-[#1b2f4a]">
            <div>
              <h3 className="text-lg font-bold text-white font-display flex items-center gap-2">
                <currentGroup.icon size={20} className="text-[#159DE8]" />
                <span>{currentGroup.name} Specialization</span>
              </h3>
              <p className="text-xs text-[#8FA0B8] mt-1">{currentGroup.description}</p>
            </div>
          </div>

          {/* Grid of Tech Items */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentGroup.items.map((tech, idx) => (
              <div 
                key={idx}
                className="p-4 rounded-xl bg-[#071426] border border-[#1b2f4a] hover:border-[#159DE8]/40 transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="text-sm font-bold text-white">{tech.name}</h4>
                  <p className="text-[11px] text-[#8FA0B8] mt-0.5">{tech.level}</p>
                </div>
                <div className="w-2 h-2 rounded-full bg-[#159DE8]/60" />
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};
